/* eslint-disable sort-exports/sort-exports */
/* eslint-disable jsdoc/require-jsdoc */
/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import type { DeviceRegistryCommand } from '@tryiris-ai/common.protojs/agent/v1/agent';
import type {
  DeviceRegistryInterfaceItemCondition,
  DeviceRegistryInterfaceItem
} from '@tryiris-ai/common.protojs/api/v1/device_settings';
import {
  DeviceRegistryCommandKey,
  DeviceRegistryInterfaceItemConditionAction,
  DeviceRegistryInterfaceItemConditionType
} from '@tryiris-ai/common.protojs/api/v1/device_settings';

import type { State as DeviceSettings } from '#~/device/settings/index.js';

/** @internal */
export interface InterfaceCondition {
  isDisabled: boolean;
  isHidden: boolean;
  key: string | undefined;
}

/** @internal */
export const calculateConditionsForItem = (
  conditions: DeviceRegistryInterfaceItemCondition[],
  deviceSettings: DeviceSettings,
  commandMap: Record<number, DeviceRegistryCommand>
): InterfaceCondition => {
  let isDisabled = false;
  let isHidden = false;
  let key = -1;

  conditions.forEach((condition) => {
    const { key: _key } = condition;
    key = _key;

    let keyValue: number | string = '';

    if (deviceSettings[_key]?.direct !== undefined) {
      keyValue = deviceSettings[_key]?.direct ?? 0;
    } else if (deviceSettings[_key]?.select !== undefined) {
      const cmd = commandMap[_key];

      const labels =
        cmd?.value?.labels.map(({ label }) => label) ??
        cmd?.select?.options.map(({ name }) => name) ??
        [];

      keyValue = labels[deviceSettings[_key]?.select ?? -1]?.toLocaleLowerCase() ?? '';
    } else if (deviceSettings[_key]?.toggle !== undefined) {
      keyValue = deviceSettings[_key]?.toggle ? 'true' : 'false';
    }

    const conditionValue = condition.value.toLocaleLowerCase();

    let conditionMet = false;

    if (condition.type === DeviceRegistryInterfaceItemConditionType.EQUALS) {
      conditionMet = keyValue === conditionValue;
    } else if (condition.type === DeviceRegistryInterfaceItemConditionType.NOT_EQUALS) {
      conditionMet = keyValue !== conditionValue;
    } else if (condition.type === DeviceRegistryInterfaceItemConditionType.ALWAYS_TRUE) {
      conditionMet = true;
    }

    if (!conditionMet) {
      return;
    }

    if (condition.action === DeviceRegistryInterfaceItemConditionAction.DISABLE) {
      isDisabled = true;
    } else if (condition.action === DeviceRegistryInterfaceItemConditionAction.HIDE) {
      isHidden = true;
    } else if (condition.action === DeviceRegistryInterfaceItemConditionAction.ENABLE) {
      isDisabled = false;
    } else if (condition.action === DeviceRegistryInterfaceItemConditionAction.SHOW) {
      isHidden = false;
    }
  });

  return { isDisabled, isHidden, key: DeviceRegistryCommandKey[key] };
};

/** @internal */
export const calculateConditions = (
  interfaceMap: Record<number, DeviceRegistryInterfaceItem>,
  commandMap: Record<number, DeviceRegistryCommand>,
  deviceSettings: DeviceSettings
): Record<number, InterfaceCondition> => {
  const conditionMap: Record<number, InterfaceCondition> = {};

  Object.entries(interfaceMap).forEach(([key, item]) => {
    const {
      isDisabled,
      isHidden,
      key: conditionKey
    } = calculateConditionsForItem(Object.values(item.conditions), deviceSettings, commandMap);

    conditionMap[Number(key)] = { isDisabled, isHidden, key: conditionKey };
  });

  return conditionMap;
};
